import { FC, useCallback, useEffect, useContext, useMemo } from 'react';
import { Box, Select } from '@robocorp/components';
import { useTranslation } from 'react-i18next';
import { VariablesContext } from '../context';
import { useActions, useSelector } from '../../../../hooks';

export const SelectArtifactId: FC = () => {
  const { endpoint, variables, setVariables } = useContext(VariablesContext);
  const { t } = useTranslation();

  const artifactsByRunId = useSelector(
    ({ workspaceResources }) => workspaceResources.artifacts.byRunId,
  );
  const loadingArtifacts = useSelector(({ entityStates }) => entityStates.artifacts.loading);

  const { getArtifacts } = useActions(({ artifactActions }) => ({
    getArtifacts: artifactActions.listArtifacts,
  }));

  const artifactItems = useMemo(
    () =>
      !variables.robotRunId || !artifactsByRunId[variables.robotRunId]
        ? []
        : Object.entries(artifactsByRunId[variables.robotRunId].byArtifactId).map(
            ([id, artifact]) => {
              return {
                label: artifact.fileName,
                value: id,
              };
            },
          ),

    [artifactsByRunId, variables],
  );

  useEffect(() => {
    if (
      variables.processId &&
      variables.processRunId &&
      variables.robotRunId &&
      endpoint.includes('artifactId')
    ) {
      getArtifacts(variables.processId, variables.processRunId, variables.robotRunId);
    }
  }, [variables.robotRunId]);

  const onChange = useCallback(
    (artifactId: string) => {
      if (!variables.robotRunId) {
        setVariables({ artifactId });
        return;
      }

      const { fileName } = artifactsByRunId[variables.robotRunId].byArtifactId[artifactId] || {};
      setVariables({ artifactId, fileName });
    },
    [artifactsByRunId, variables, setVariables],
  );

  let placeholder = artifactItems.length ? `${t('choose')}...` : t('noArtifactsForRun');

  if (loadingArtifacts) {
    placeholder = t('loading');
  }

  return !endpoint.includes('artifactId') ? null : (
    <Box mb={20}>
      <Select
        label={t('chooseArtifact')}
        items={artifactItems}
        onChange={onChange}
        disabled={loadingArtifacts || !variables.processId || artifactItems.length <= 0}
        value={variables.artifactId}
        placeholder={placeholder}
      />
    </Box>
  );
};
